/* eslint-disable @typescript-eslint/no-unused-vars */
import {
    Button,
    Flex,
    Modal,
    NumberInput,
    Select,
    Stack,
    Text,
    MultiSelect,
    Box,
    Alert,
    Avatar,
    ComboboxItem,
    ComboboxLikeRenderOptionInput
} from "@mantine/core";
import { IconUserPlus, IconUsers, IconAlertCircle } from "@tabler/icons-react";
import { useEffect, useState, useMemo } from "react";
import { useWorkspaceMembers } from "../../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import { Checkbox as DsCheckbox } from "../../../../../components/ui/checkbox";
import notify from "../../../../../utils/notify";

type Props = {
    onCompleteAction: (action: any, type: string) => void;
    opened: boolean;
    close: () => void;
    data?: any;
    signal?: any;
};

const assignModeChoices = [
    { value: "least_accounts", label: "Least Accounts (Balance Load)" },
    { value: "least_revenue", label: "Least Revenue (Balance ARR)" },
    { value: "round_robin", label: "Round Robin (Rotate)" },
];

const keyRoleChoices = [
    { value: "csm_id", label: "CSM" },
    { value: "ae_id", label: "Account Executive" },
    { value: "account_champion_id", label: "Account Champion" },
];

const scopeEntityChoices = [
    { value: "accounts", label: "Accounts" },
    { value: "companies", label: "Companies" },
];

// Custom Item for MultiSelect
/* eslint-disable react/jsx-props-no-spreading */
const SelectOption = ({ option }: { option: any }) => (
    <Flex gap="sm" align="center">
        <Avatar src={option.avatar_url} radius="xl" size="sm" color="initials" name={option.label} />
        <Box>
            <Text size="sm" fw={500}>{option.label}</Text>
            <Text size="xs" c="dimmed">{option.email}</Text>
        </Box>
    </Flex>
);

const AssignPersonModal = ({
    onCompleteAction,
    opened,
    close,
    data,
    signal,
}: Props) => {
    const workspace = useWorkspaceStore((state) => state.workspace);

    const [assignMode, setAssignMode] = useState<string>(
        (data?.assign_mode === "specific" ? "least_accounts" : data?.assign_mode) || "least_accounts"
    );
    const [keyRoleKey, setKeyRoleKey] = useState<string>(data?.key_role_default_key || "csm_id");
    const [scopeEntity, setScopeEntity] = useState<string>(data?.scope_entity || "accounts");
    const [maxAccountsPerUser, setMaxAccountsPerUser] = useState<number | "">(data?.max_accounts_per_user || "");

    // If incoming data has specific users or if assign_mode was 'specific', enable the limiter
    const [limitToSpecificUsers, setLimitToSpecificUsers] = useState<boolean>(
        data?.assign_mode === "specific" || (data?.user_ids && data?.user_ids.length > 0)
    );
    const [userIds, setUserIds] = useState<string[]>(data?.user_ids || []);
    const [teamId, setTeamId] = useState<string>(data?.team_id || "");
    const [error, setError] = useState<boolean>(false);

    // Fetch workspace members
    const { data: membersData, isLoading: isLoadingMembers } = useWorkspaceMembers({
        workspaceId: workspace?.id,
        enabled: !!workspace?.id && opened,
    });

    // Safe access to members list supporting both 'members' and 'users' properties
    const memberList = useMemo(() => {
        if (!membersData) return [];
        return membersData.members || membersData.users || [];
    }, [membersData]);

    const memberOptions = useMemo(() => memberList.map((member: any) => ({
        value: member.user_id || member.id,
        label: member.first_name && member.last_name
            ? `${member.first_name} ${member.last_name}`
            : member.email || "Unknown User",
        email: member.email,
        avatar_url: member.avatar_url,
        name: member.first_name && member.last_name
            ? `${member.first_name} ${member.last_name}`
            : member.email || "Unknown User",
    })), [memberList]);

    // Reset state when modal opens with new data
    useEffect(() => {
        if (opened) {
            setAssignMode((data?.assign_mode === "specific" ? "least_accounts" : data?.assign_mode) || "least_accounts");
            setKeyRoleKey(data?.key_role_default_key || "csm_id");
            setScopeEntity(data?.scope_entity || "accounts");
            setMaxAccountsPerUser(data?.max_accounts_per_user || "");

            const hasSpecificUsers = (data?.user_ids && data?.user_ids.length > 0) || !!data?.team_id;
            setLimitToSpecificUsers(data?.assign_mode === "specific" || hasSpecificUsers);

            setUserIds(data?.user_ids || []);
            setTeamId(data?.team_id || "");
            setError(false);
        }
    }, [opened, data]);

    const handleSave = () => {
        // Validation
        if (!assignMode || !keyRoleKey) {
            setError(true);
            return;
        }

        if (limitToSpecificUsers) {
            if (userIds.length === 0 && !teamId) {
                setError(true);
                notify({
                    messageList: ["Please select at least one user or a team when limiting to specific people."],
                    type: "failure",
                });
                return;
            }
        }

        const actionPayload: any = {
            type: "assign_person",
            assign_mode: assignMode,
            key_role_default_key: keyRoleKey,
            scope_entity: scopeEntity,
        };

        if (maxAccountsPerUser !== "") {
            actionPayload.max_accounts_per_user = Number(maxAccountsPerUser);
        }

        if (limitToSpecificUsers) {
            if (userIds.length > 0) actionPayload.user_ids = userIds;
            if (teamId) actionPayload.team_id = teamId;
        }

        onCompleteAction(actionPayload, "assign_person");
        notify({
            messageList: ["Assignment action added successfully"],
            type: "success",
        });
        close();
    };

    return (
        <Modal
            padding="lg"
            classNames={{
                title: "text-gray-900 text-xl font-bold",
            }}
            size={600}
            opened={opened}
            centered
            onClose={close}
            title={
                <Flex align="center">
                    <IconUserPlus className="w-6 h-6 mr-4 text-gray-600" />
                    <Stack gap={0}>
                        <Text>Key Role Assigner</Text>
                        <Text size="xs" c="dimmed" fw={500}>
                            Automatically assign key roles like CSM or AE to accounts.
                        </Text>
                    </Stack>
                </Flex>
            }
        >
            <Stack gap="md">
                <Select
                    label="Assignment Strategy"
                    description="How should we distribute the assignments?"
                    data={assignModeChoices}
                    value={assignMode}
                    onChange={(val) => setAssignMode(val || "least_accounts")}
                    allowDeselect={false}
                />

                <Select
                    label="Role to Assign"
                    description="Which key role field should be updated?"
                    data={keyRoleChoices}
                    value={keyRoleKey}
                    onChange={(val) => setKeyRoleKey(val || "csm_id")}
                    allowDeselect={false}
                />

                <Select
                    label="Scope"
                    description="Apply assignment to Accounts or Companies?"
                    data={scopeEntityChoices}
                    value={scopeEntity}
                    onChange={(val) => setScopeEntity(val || "accounts")}
                    allowDeselect={false}
                />

                <label className="flex cursor-pointer items-start gap-2">
                    <DsCheckbox
                        checked={limitToSpecificUsers}
                        onCheckedChange={(checked) => setLimitToSpecificUsers(checked === true)}
                        className="mt-0.5 h-4 w-4"
                    />
                    <span className="text-[13px] leading-[1.4] text-[var(--mantine-color-text)]">
                        Limit assignment to specific users
                    </span>
                </label>

                {limitToSpecificUsers && (
                    <Box className="p-4 bg-gray-50 rounded-md border border-gray-200">
                        <Text size="sm" fw={600} mb="xs">Candidate Users</Text>
                        <Text size="xs" c="dimmed" mb="md">
                            Select specific users or a team to distribute assignments among using the selected strategy.
                        </Text>

                        <MultiSelect
                            label="Select Users"
                            placeholder="Search users..."
                            data={memberOptions}
                            value={userIds}
                            onChange={setUserIds}
                            searchable
                            clearable
                            mb="sm"
                            renderOption={SelectOption}
                            maxDropdownHeight={300}
                        />

                        {/* Team selection placeholder - assuming team API integration later if needed */}
                        {/* <Select
              label="Select Team"
              placeholder="Choose a team..."
              data={[]} 
              value={teamId}
              onChange={(val) => setTeamId(val || "")}
              disabled
              description="Team selection coming soon"
            /> */}

                        {error && userIds.length === 0 && !teamId && (
                            <Text c="red" size="xs" mt={5}>
                                * At least one user or team is required when limiting users.
                            </Text>
                        )}
                    </Box>
                )}

                {assignMode === "least_revenue" && (
                    <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
                        Ensure your workspace has the &apos;ARR&apos; field populated for this strategy to work effectively.
                    </Alert>
                )}

                <NumberInput
                    label="Max Assignments Per Run (Optional)"
                    description="Limit the number of new assignments per user in a single run."
                    placeholder="e.g. 5"
                    value={maxAccountsPerUser}
                    onChange={(val) => setMaxAccountsPerUser(val === "" ? "" : Number(val))}
                    min={1}
                />

                <Flex justify="flex-end" gap="sm" mt="lg">
                    <Button variant="outline" color="gray" onClick={close}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} color="blue">
                        Save Action
                    </Button>
                </Flex>
            </Stack>
        </Modal>
    );
};

export default AssignPersonModal;
